import { useQuery } from '@tanstack/react-query';

import {
  type CONTENT_TYPES,
  type PerContentListProps,
} from '@hivebrite/design-system/ContentList';
import { toast } from '@hivebrite/design-system/Toast';

import {
  RESULTS_LIMIT,
  MIN_SCORE_THRESHOLD,
  AVAILABLE_CONTENT_TYPES,
} from 'hb-react/shared/components/ContentRecommendation/constants';
import { useNavigateToContentItem } from 'hb-react/shared/components/ContentRecommendation/hooks/useNavigateToContentItem';
import { getRecommendedContents } from 'hb-react/shared/components/ContentRecommendation/requests';
import {
  type AvailableContentTypes,
  type RecommendedContents,
} from 'hb-react/shared/components/ContentRecommendation/types';
import { formatRecommendedContentData } from 'hb-react/shared/components/ContentRecommendation/utils';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';

interface UseGetRecommendedContentsProps {
  contentId: number;
  contentType: AvailableContentTypes;
  groupId?: number;
  recommendedContentTypes: Array<AvailableContentTypes>;
}

export const useGetRecommendedContents = ({
  contentId,
  contentType,
  groupId,
  recommendedContentTypes,
}: UseGetRecommendedContentsProps): {
  data: Array<PerContentListProps[CONTENT_TYPES]>;
  isLoading: boolean;
  refetch: () => void;
} => {
  const navigateToContentItem = useNavigateToContentItem();
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const { flagUjAiContentRecommendationForumEvents } = useFeatureFlag();

  // Allow only News recommendations unless flag-uj-ai-content-recommendation-forum-events is set.
  const contentTypes = flagUjAiContentRecommendationForumEvents
    ? recommendedContentTypes
    : [AVAILABLE_CONTENT_TYPES.NEWS];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contentRecommendations', contentType, contentId],
    queryFn: async ({ signal }) => {
      try {
        const {
          data: { data },
        } = await getRecommendedContents({
          params: {
            contentId,
            contentType,
            contentTypes,
            limit: RESULTS_LIMIT,
            scoreThreshold: MIN_SCORE_THRESHOLD,
            ...(groupId && { groupId }),
          },
          signal,
        });

        return data.map(
          (recommendedItem: RecommendedContents[AvailableContentTypes]) => ({
            // TODO: Once serializers are stable, migrate this new format in front-monorepo
            ...formatRecommendedContentData(
              recommendedItem,
              timeZoneConfiguration,
            ),
            onClick: () => navigateToContentItem(recommendedItem),
          }),
        );
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        throw error;
      }
    },
    enabled: !!contentType,
    retry: false,
  });

  return {
    data: data || [],
    isLoading,
    refetch,
  };
};
